import * as THREE from 'three';

// PingLayer. Renders a transient "look here" pulse at a world-space
// point on the game board. One shape, one animation: a concentric ring
// emits three overlapping pulses over PING_DURATION_MS and a couple of
// upward-drifting motes give it presence in 3D.
//
// Lifecycle is driven by a single scene.onFrame subscription that ticks
// every active ping; finished pings are recycled into a fixed-size pool
// so there are no per-fire allocations.
//
// No knowledge of cards, players, or the network. Fire it with a world
// point and a color and it draws the ring. Callers (GameBoard) wire the
// local-echo + receive-side handlers around it.

const POOL_SIZE = 6;
const PING_DURATION_MS = 1600;
const REDUCED_PING_DURATION_MS = 800;
const PULSE_COUNT = 3;
const PULSE_OVERLAP = 0.3; // each pulse starts 70% through the previous
// Ring covers slightly more than a card width at peak (cards are ~11
// world units wide). Bigger than a card so the eye registers it from
// anywhere on the board.
const RING_INNER_RADIUS_FROM = 1.5;
const RING_INNER_RADIUS_TO = 7.0;
const RING_RADIAL_WIDTH = 0.5;
const RING_Y_OFFSET = 0.12;
const MOTE_COUNT = 2;
const MOTE_RISE = 3.0;
const MOTE_DURATION_MS = 900;
// Bright warm gold. Used with additive blending against the dark felt
// so it reads as a glowing light, not a flat color. Identity is carried
// by audio volume (own quieter than opponent's), not by hue.
const PING_COLOR = 0xffd24a;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

// Opacity profile for a single pulse: ramps up over the first 30% then
// fades to zero. Multiplied by 0.9 peak so even three overlapping pulses
// stay below pure white.
function pulseOpacity(p) {
  if (p < 0.3) return (p / 0.3) * 0.9;
  return (1 - (p - 0.3) / 0.7) * 0.9;
}

// depthTest: false + renderOrder: 999 is the Three.js idiom for "draw
// on top of everything in the scene." With cards regularly stacking and
// overlapping on the felt, a depth-tested ring would constantly hide
// behind whatever the player clicked on. Defeating the whole point of
// "look here." Skipping depth-test makes the ring always visible.
function createRingMesh() {
  // Geometry is rebuilt each frame (cheap. A few dozen vertices) so the
  // ring can animate its inner/outer radii independently per pulse.
  const geom = new THREE.RingGeometry(1, 1.05, 48, 1);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.rotation.x = -Math.PI / 2; // lay flat on the world XZ plane
  mesh.visible = false;
  mesh.renderOrder = 999;
  return mesh;
}

function createMoteMesh() {
  const geom = new THREE.SphereGeometry(0.4, 10, 10);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.visible = false;
  mesh.renderOrder = 999;
  return mesh;
}

/**
 * Create a ping renderer bound to a Three.js scene.
 *
 * @param {THREE.Scene} scene
 * @param {{ onFrame: (cb: (dt: number) => void) => (() => void) }} frameSource
 *   The tableScene's frame-callback registrar. Lets us tick every
 *   active ping once per render frame without owning our own rAF loop.
 */
export function createPingLayer(scene, frameSource) {
  const pool = [];
  for (let i = 0; i < POOL_SIZE; i++) {
    const ring = createRingMesh();
    scene.add(ring);
    const motes = [];
    for (let m = 0; m < MOTE_COUNT; m++) {
      const mote = createMoteMesh();
      scene.add(mote);
      motes.push(mote);
    }
    pool.push({
      ring,
      motes,
      ringMat: ring.material,
      moteMats: motes.map((m) => m.material),
      active: false,
      startedAt: 0,
      durationMs: PING_DURATION_MS,
      reduced: false,
      // World position + color captured at fire time so the ring stays
      // anchored even if the caller is no longer holding a reference.
      x: 0, y: 0, z: 0,
      color: new THREE.Color(0xffffff),
      moteOffsets: [],
    });
  }

  let nextEvictionIndex = 0;

  function findSlot() {
    for (const slot of pool) {
      if (!slot.active) return slot;
    }
    // Pool exhausted: recycle the oldest in-flight ring. Rotate the
    // eviction pointer so the same slot isn't always recycled when the
    // entire pool overlaps in time.
    const slot = pool[nextEvictionIndex];
    nextEvictionIndex = (nextEvictionIndex + 1) % POOL_SIZE;
    return slot;
  }

  function fire({ x, y, z }) {
    const slot = findSlot();
    slot.active = true;
    slot.startedAt = performance.now();
    slot.reduced = prefersReducedMotion();
    slot.durationMs = slot.reduced ? REDUCED_PING_DURATION_MS : PING_DURATION_MS;
    slot.x = x;
    slot.y = y + RING_Y_OFFSET;
    slot.z = z;
    slot.color.setHex(PING_COLOR);
    slot.ring.position.set(slot.x, slot.y, slot.z);
    slot.ringMat.color.copy(slot.color);
    slot.ring.visible = true;

    // Stagger motes around the ping point with a small random offset so
    // they don't all rise from the exact same pixel.
    slot.moteOffsets = [];
    for (let m = 0; m < MOTE_COUNT; m++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 0.3 + Math.random() * 0.5;
      slot.moteOffsets.push({
        dx: Math.cos(angle) * radius,
        dz: Math.sin(angle) * radius,
        delay: m * 120,
      });
      const mote = slot.motes[m];
      const mat = slot.moteMats[m];
      mat.color.copy(slot.color);
      mote.position.set(slot.x + slot.moteOffsets[m].dx, slot.y, slot.z + slot.moteOffsets[m].dz);
      mote.visible = !slot.reduced;
      mat.opacity = 0;
    }
  }

  function update() {
    const now = performance.now();
    for (const slot of pool) {
      if (!slot.active) continue;
      const elapsed = now - slot.startedAt;
      const t = elapsed / slot.durationMs;
      if (t >= 1) {
        slot.active = false;
        slot.ring.visible = false;
        for (const mote of slot.motes) mote.visible = false;
        slot.ringMat.opacity = 0;
        continue;
      }

      // Composite the overlapping pulses into a single max-opacity ring
      // each frame. Each pulse owns a window of length pulseLen, and
      // the windows overlap by PULSE_OVERLAP.
      const pulseCount = slot.reduced ? 1 : PULSE_COUNT;
      const pulseLen = 1 / (pulseCount - (pulseCount - 1) * PULSE_OVERLAP);
      const step = pulseLen * (1 - PULSE_OVERLAP);
      let bestOpacity = 0;
      let bestInner = RING_INNER_RADIUS_FROM;
      for (let i = 0; i < pulseCount; i++) {
        const pStart = i * step;
        const p = (t - pStart) / pulseLen;
        if (p < 0 || p > 1) continue;
        const op = pulseOpacity(p);
        if (op > bestOpacity) {
          bestOpacity = op;
          const eased = easeOutCubic(p);
          bestInner = RING_INNER_RADIUS_FROM + (RING_INNER_RADIUS_TO - RING_INNER_RADIUS_FROM) * eased;
        }
      }

      slot.ringMat.opacity = bestOpacity;

      // Rebuilding the ring geometry per frame is fine here: 48 segments
      // × 6 active slots × 60fps ≈ 17k tris/sec, negligible. Disposing
      // and replacing keeps the inner/outer radii independent of mesh
      // scale, which would otherwise stretch the radial line width too.
      const oldGeom = slot.ring.geometry;
      slot.ring.geometry = new THREE.RingGeometry(bestInner, bestInner + RING_RADIAL_WIDTH, 48, 1);
      oldGeom.dispose();

      // Motes rise and fade independently of the ring pulses.
      if (!slot.reduced) {
        for (let m = 0; m < MOTE_COUNT; m++) {
          const mote = slot.motes[m];
          const mat = slot.moteMats[m];
          const moteElapsed = elapsed - slot.moteOffsets[m].delay;
          if (moteElapsed < 0) { mat.opacity = 0; continue; }
          const mp = Math.min(1, moteElapsed / MOTE_DURATION_MS);
          mote.position.y = slot.y + easeOutCubic(mp) * MOTE_RISE;
          mat.opacity = (1 - mp) * 0.8;
        }
      }
    }
  }

  const unsubscribe = frameSource.onFrame(update);

  function dispose() {
    unsubscribe?.();
    for (const slot of pool) {
      scene.remove(slot.ring);
      slot.ring.geometry.dispose();
      slot.ringMat.dispose();
      for (let m = 0; m < MOTE_COUNT; m++) {
        scene.remove(slot.motes[m]);
        slot.motes[m].geometry.dispose();
        slot.moteMats[m].dispose();
      }
    }
    pool.length = 0;
  }

  return { fire, dispose };
}
